import {
  Button,
  EmptyTableState,
  Input,
  Pagination,
  Table,
  Tooltip,
  type TableColumn,
  type TableProps,
} from "ds/ui";
import { Selection, SortDescriptor } from "@heroui/react";
import clsx from "clsx";
import { getAgeDisplayFromBirthDate } from "core/model/utils/dates";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  ClinicalPatient,
  ClinicalPatientProfile,
  GetOntologyType,
} from "core/types";
import { useHasOverflown } from "dsl/hooks";
import { debounce } from "lodash";
import { SearchIcon, UserPlusIcon } from "lucide-react";
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslations } from "translations";

export type DoctorDashboardTableRow = {
  id: ClinicalPatient["id"];
  firstName?: ClinicalPatientProfile["first_name"];
  lastName?: ClinicalPatientProfile["last_name"];
  externalId: ClinicalPatient["external_id"];
  userId: ClinicalPatient["user_id"];
  birthDate?: ClinicalPatientProfile["birth_date"];
  gender: ClinicalPatientProfile["gender"];
  mainDiagnosisLine1: string;
  mainDiagnosisLine2: string;
  dischargeDate: ClinicalPatientProfile["discharge_date"];
};

type ColumnLabels = {
  columnPatient: string;
  columnAge: string;
  columnGender: string;
  columnMainDiagnosis: string;
};

export type DoctorDashboardTableProps = Pick<
  TableProps<DoctorDashboardTableRow>,
  | "data"
  | "emptyContent"
  | "isLoading"
  | "page"
  | "rowsPerPage"
  | "rowsPerPageOptions"
  | "showPagination"
  | "total"
  | "onPageChange"
  | "onRowAction"
  | "onRowsPerPageChange"
  | "onSortChange"
> & {
  onImportPatient?: () => void;
  onSearchChange?: (value: string) => void;
  search?: string;
  title?: ReactNode;
};

type TruncationTooltipProps = {
  children: ReactNode;
  className?: string;
  content: string;
};

const SEARCH_DEBOUNCE_MS = 300;

const TABLE_CLASS_NAMES: TableProps<DoctorDashboardTableRow>["classNames"] = {
  table: "table-fixed",
  td: "min-h-14 border-b border-gray-200 bg-white px-0 py-4 text-body-md",
  th: "h-14 border-b border-gray-500 bg-white px-0 py-4 text-body-md font-bold leading-5 text-gray-900",
  wrapper: "rounded-lg bg-white shadow-sm p-0",
} as const;

const CELL_LINE1 = "truncate text-heading-sm font-semibold text-gray-900";
const CELL_LINE2 = "truncate text-body-md leading-5 text-gray-600";

const EMPTY_PLACEHOLDER = "\u00A0";

function withPlaceholder(value: string | undefined | null): string {
  return value?.trim() ? value : EMPTY_PLACEHOLDER;
}

function isEmptyValue(value: string | undefined | null): boolean {
  return !value?.trim();
}

export function TruncationTooltip({
  children,
  className,
  content,
}: TruncationTooltipProps) {
  const { hasOverflown, ref } = useHasOverflown<HTMLDivElement>({
    checkChildren: true,
    direction: "x",
    value: content,
  });

  return (
    <Tooltip
      content={
        hasOverflown ? (
          <span className="block max-w-sm text-wrap whitespace-pre-wrap">
            {content}
          </span>
        ) : (
          ""
        )
      }
      isDisabled={!hasOverflown}
      placement="top"
    >
      <div
        ref={ref}
        className={clsx("w-full min-w-0 overflow-hidden", className)}
      >
        {children}
      </div>
    </Tooltip>
  );
}

function getDoctorDashboardColumns({
  getOntology,
  labels,
  unknownLabel,
}: {
  getOntology: GetOntologyType;
  labels: ColumnLabels;
  unknownLabel: string;
}): TableColumn<DoctorDashboardTableRow>[] {
  return [
    {
      allowsSorting: false,
      key: "firstName",
      label: labels.columnPatient,
      render: (row) => {
        const line1 = [row.firstName?.decrypted, row.lastName?.decrypted]
          .filter(Boolean)
          .join(" ");
        const line2 = [row.externalId, row.userId].filter(Boolean).join(" | ");
        const tooltipContent = [line1, line2].filter(Boolean).join("\n");
        return (
          <TruncationTooltip
            className="flex flex-col gap-0.5"
            content={tooltipContent}
          >
            <span
              aria-hidden={isEmptyValue(line1)}
              className={CELL_LINE1}
              data-testid="doctor-dashboard-cell-patient-line1"
            >
              {withPlaceholder(line1)}
            </span>
            <span
              aria-hidden={isEmptyValue(line2)}
              className={CELL_LINE2}
              data-testid="doctor-dashboard-cell-patient-line2"
            >
              {withPlaceholder(line2)}
            </span>
          </TruncationTooltip>
        );
      },
    },
    {
      allowsSorting: false,
      key: "gender",
      label: labels.columnGender,
      render: (row) => (
        <span className={CELL_LINE2} data-testid="doctor-dashboard-cell-gender">
          {getOntology({ key: row.gender, type: "gender" })}
        </span>
      ),
    },
    {
      allowsSorting: false,
      key: "birthDate",
      label: labels.columnAge,
      render: (row) => (
        <span className={CELL_LINE2}>
          {getAgeDisplayFromBirthDate(row.birthDate, unknownLabel)}
        </span>
      ),
    },
    {
      allowsSorting: false,
      key: "mainDiagnosisLine1",
      label: labels.columnMainDiagnosis,
      render: (row) => {
        const tooltipContent = [row.mainDiagnosisLine1, row.mainDiagnosisLine2]
          .filter(Boolean)
          .join("\n");
        return (
          <TruncationTooltip
            className="flex flex-col gap-0.5 overflow-hidden"
            content={tooltipContent}
          >
            <span
              aria-hidden={isEmptyValue(row.mainDiagnosisLine1)}
              className={clsx(CELL_LINE1, "truncate")}
            >
              {withPlaceholder(row.mainDiagnosisLine1)}
            </span>
            <span
              aria-hidden={isEmptyValue(row.mainDiagnosisLine2)}
              className={clsx(CELL_LINE2, "truncate")}
            >
              {withPlaceholder(row.mainDiagnosisLine2)}
            </span>
          </TruncationTooltip>
        );
      },
    },
  ];
}

const DoctorDashboardTableHeader = memo(function DoctorDashboardTableHeader({
  onImportPatient,
  onSearchChange,
  search,
}: {
  onImportPatient?: () => void;
  onSearchChange?: (value: string) => void;
  search: string;
}) {
  const translations = useTranslations();
  const [localSearch, setLocalSearch] = useState(search);

  const debouncedOnSearchChange = useMemo(
    () =>
      onSearchChange
        ? debounce((value: string) => onSearchChange(value), SEARCH_DEBOUNCE_MS)
        : undefined,
    [onSearchChange],
  );

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(
    () => () => debouncedOnSearchChange?.cancel(),
    [debouncedOnSearchChange],
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      debouncedOnSearchChange?.(value);
    },
    [debouncedOnSearchChange],
  );

  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <h1
        className="text-heading-xl text-gray-900"
        data-testid="doctor-dashboard-heading"
      >
        {translations.clinicalDocumentation.title}
      </h1>
      <div className="flex flex-wrap items-center justify-end gap-6">
        <Input
          aria-label={translations.clinicalDocumentation.searchPlaceholder}
          className="w-[280px]"
          name="doctor-dashboard-search"
          placeholder={translations.clinicalDocumentation.searchPlaceholder}
          size="md"
          startContent={
            <SearchIcon className="size-4 shrink-0 text-gray-600" />
          }
          value={localSearch}
          onValueChange={handleValueChange}
        />
        {onImportPatient ? (
          <Button
            color="primary"
            data-testid="doctor-dashboard-import-patient"
            id="doctor-dashboard-import-patient"
            size="md"
            startContent={<UserPlusIcon className="size-4" />}
            variant="solid"
            onPress={onImportPatient}
          >
            {translations.clinicalDocumentation.addPatientButton}
          </Button>
        ) : null}
      </div>
    </div>
  );
});

export function DoctorDashboardTable({
  data,
  emptyContent,
  isLoading = false,
  onImportPatient,
  onPageChange,
  onRowAction,
  onRowsPerPageChange,
  onSearchChange,
  onSortChange,
  page = 1,
  rowsPerPage = 10,
  rowsPerPageOptions,
  search = "",
  showPagination = true,
  total,
}: DoctorDashboardTableProps) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>();
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

  const topContent = useMemo(
    () => (
      <DoctorDashboardTableHeader
        search={search}
        onImportPatient={onImportPatient}
        onSearchChange={onSearchChange}
      />
    ),
    [onImportPatient, onSearchChange, search],
  );

  const columnLabels = useMemo<ColumnLabels>(
    () => ({
      columnAge: translations.clinicalDocumentation.columnAge,
      columnGender: translations.clinicalDocumentation.columnGender,
      columnMainDiagnosis:
        translations.clinicalDocumentation.columnMainDiagnosis,
      columnPatient: translations.clinicalDocumentation.columnPatient,
    }),
    [translations],
  );

  const columns = useMemo(
    () =>
      getDoctorDashboardColumns({
        getOntology,
        labels: columnLabels,
        unknownLabel: translations.general.unknown,
      }),
    [columnLabels, getOntology, translations],
  );

  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
    onSortChange?.(descriptor);
  };

  const defaultEmptyContent = (
    <EmptyTableState
      firstLine={translations.clinicalDocumentation.title}
      secondLine={translations.predict.dashboard.placeholderNoDataSecondLine}
    />
  );

  const classNameHeader = (column: TableColumn<DoctorDashboardTableRow>) => {
    if (column.key === "firstName") return "w-[50%] pl-6 pr-3 py-2";
    if (column.key === "gender") return "w-[10%] px-3 py-2";
    if (column.key === "birthDate") return "w-[10%] px-3 py-2";
    if (column.key === "mainDiagnosisLine1")
      return "w-[30%] min-w-0 pl-3 pr-6 py-2";
    return undefined;
  };

  const classNameCell = (
    column: TableColumn<DoctorDashboardTableRow>,
    _item: DoctorDashboardTableRow,
  ): string | undefined => {
    if (column.key === "firstName") return "w-[50%] pl-6 pr-3 py-2";
    if (column.key === "gender") return "w-[10%] px-3 py-2";
    if (column.key === "birthDate") return "w-[10%] px-3 py-2";
    if (column.key === "mainDiagnosisLine1")
      return "w-[30%] min-w-0 overflow-hidden pl-3 pr-6 py-2";
    return undefined;
  };

  const paginationTotal = total ?? data?.length ?? 0;
  const bottomContent =
    showPagination && paginationTotal > 0 ? (
      <div className="flex justify-end py-4">
        <Pagination
          isCentered={false}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          showControls
          total={paginationTotal}
          variant="bordered"
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
    ) : undefined;

  return (
    <Table<DoctorDashboardTableRow>
      aria-label={translations.clinicalDocumentation.title}
      bottomContent={bottomContent}
      classNameCell={classNameCell}
      classNameHeader={classNameHeader}
      classNames={TABLE_CLASS_NAMES}
      columns={columns}
      data={data ?? []}
      emptyContent={emptyContent ?? defaultEmptyContent}
      isLoading={isLoading}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      rowTestIdPrefix="patient"
      selectedKeys={selectedKeys}
      showPagination={showPagination}
      sortDescriptor={sortDescriptor}
      topContent={topContent}
      total={paginationTotal}
      onPageChange={onPageChange}
      onRowAction={onRowAction}
      onRowsPerPageChange={onRowsPerPageChange}
      onSelectionChange={setSelectedKeys}
      onSortChange={handleSortChange}
    />
  );
}
